// ════════════════════════════════════════════════════════════
// useTheme — ECR
// ════════════════════════════════════════════════════════════
// Synchronise la classe `.dark` sur `<html>` avec le state
// du store. Si theme = 'system', suit `prefers-color-scheme`
// et réagit aux changements de préférence OS.
// ════════════════════════════════════════════════════════════
import { useEffect } from 'react';
import { useAppStore, type Theme } from '@/store/useAppStore';

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  root.classList.toggle('dark', isDark);
}

/**
 * Synchronise la classe `.dark` sur <html> avec le theme du store.
 * À monter dans le composant racine (Layout ou _authed).
 *
 * `forced` ('light' | 'dark') ignore le store et impose le thème — utilisé
 * pour forcer le mode clair côté pointeur (terrain, jamais de dark par défaut).
 */
export function useTheme(forced?: 'light' | 'dark'): {
  theme: Theme;
  setTheme: (t: Theme) => void;
  resolvedTheme: 'light' | 'dark';
} {
  const storeTheme = useAppStore((s) => s.theme);
  const setTheme = useAppStore((s) => s.setTheme);
  const active: Theme = forced ?? storeTheme;

  // Applique le theme à chaque changement
  useEffect(() => {
    applyTheme(active);
  }, [active]);

  // Si "system", écouter les changements de préférence OS
  useEffect(() => {
    if (active !== 'system') return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      applyTheme('system');
    };

    mq.addEventListener('change', onChange);
    return () => {
      mq.removeEventListener('change', onChange);
    };
  }, [active]);

  const resolvedTheme: 'light' | 'dark' =
    active === 'dark' ||
    (active === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches)
      ? 'dark'
      : 'light';

  return { theme: storeTheme, setTheme, resolvedTheme };
}
